import { useNavigate, useSearchParams } from "react-router";
import { Badge, PageHeader } from "@nelurio/ui";
import { useI18n } from "@nelurio/i18n";
import { useAdminResource } from "../hooks";
import { EmptyHint, ErrorState, LoadingState } from "../components";
import type { FeedbackRow } from "../types";

function FeedbackCard({ item }: { item: FeedbackRow }) {
  const { t, formatDate } = useI18n();
  const navigate = useNavigate();
  const openChat = item.email
    ? () => navigate(`/feedback/chat?email=${encodeURIComponent(item.email ?? "")}`)
    : undefined;

  const categoryText = (category: string) =>
    category === "suggestion"
      ? t("admin.feedback.categorySuggestion")
      : category === "complaint"
        ? t("admin.feedback.categoryComplaint")
        : category === "bug"
          ? t("admin.feedback.categoryBug")
          : category === "other"
            ? t("admin.feedback.categoryOther")
            : category;

  const statusText = (status: string) =>
    status === "open"
      ? t("admin.feedback.statusOpen")
      : status === "reviewed"
        ? t("admin.feedback.statusReplied")
        : status;

  return (
    <section
      className={`nl-card chat-card ${openChat ? "is-clickable" : ""}`}
      onClick={openChat}
      role={openChat ? "button" : undefined}
      tabIndex={openChat ? 0 : undefined}
      onKeyDown={openChat ? (event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); openChat(); } } : undefined}
      aria-label={openChat ? t("admin.feedback.openChatAria") : undefined}
    >
      <span className="chat-card__head">
        <span className="chat-card__avatar" aria-hidden="true">{(item.email || "?")[0].toUpperCase()}</span>
        <span className="chat-card__meta">
          <strong className="admin-wrap">{item.email || t("admin.feedback.anonymous")}</strong>
          <small>{categoryText(item.category)}{item.page ? ` · ${item.page}` : ""} · {formatDate(item.createdAt)}</small>
        </span>
        <Badge tone={item.status === "open" ? "amber" : item.status === "reviewed" || item.status === "replied" ? "green" : "neutral"}>
          {statusText(item.status)}
        </Badge>
      </span>
      <div className="chat-card__thread">
        <div className="chat-bubble chat-bubble--user admin-wrap">{item.message}</div>
        {item.reply && (
          <div className="chat-bubble chat-bubble--support admin-wrap">
            {item.reply}
            <time>{formatDate(item.repliedAt)}</time>
          </div>
        )}
      </div>
    </section>
  );
}

export function FeedbackPage() {
  const { t } = useI18n();
  const feedback = useAdminResource<FeedbackRow[]>("/feedback?limit=100");
  const [params] = useSearchParams();
  const appFilter = params.get("app") ?? "";
  const rows = (feedback.data ?? []).filter((r) => !appFilter || r.appSlug === appFilter);

  return (
    <div className="admin-page">
      <PageHeader title={t("admin.feedback")} subtitle={t("admin.feedback.subtitle")} />

      {feedback.status === "error" && <ErrorState message={feedback.error} onRetry={() => void feedback.reload()} />}
      {feedback.status === "loading" && <LoadingState rows={4} />}
      {feedback.status === "ready" && (
        rows.length === 0
          ? <EmptyHint>{t("admin.feedback.empty")}</EmptyHint>
          : <div className="admin-feedback-list">{rows.map((item) => <FeedbackCard key={item.id} item={item} />)}</div>
      )}
    </div>
  );
}
